import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { BASE_URL } from "../config/apiConfig";

export async function exportApplicationExcel(
    rows,
    fileName = "Incentive_Application.xlsx"
) {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Application");

    /* ---------------- HEADERS ---------------- */
    ws.addRow(["Section", "SubSection", "Field", "Value"]);
    const headerRow = ws.getRow(1);
    headerRow.font = { bold: true, size: 12 };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };
    headerRow.eachCell((cell) => {
        cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "CCFFCC" },
        };
        cell.border = {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
        };
    });

    /* ---------------- DATA ROWS ---------------- */
    rows.forEach((row) => {
        const r = ws.addRow([row.Section, row.SubSection, row.Field, row.Value]);

        // Bold Section + Field
        r.getCell(1).font = { bold: true };
        r.getCell(3).font = { bold: true };

        // Wrap Value
        r.getCell(4).alignment = { wrapText: true, vertical: "top" };

        // Borders
        r.eachCell((cell) => {
            cell.border = {
                top: { style: "thin" },
                bottom: { style: "thin" },
                left: { style: "thin" },
                right: { style: "thin" },
            };
        });

        // File hyperlinks
        if (row.__type === "FILE" || (typeof row.Value === "object" && "docTypeId" in row.Value)) {
            const r = ws.lastRow;
            const fileObj = row.Value;

            if (fileObj && fileObj.url) {
                r.getCell(4).value = {
                    text: fileObj.name || fileObj.label || "View File",
                    hyperlink: BASE_URL + "/" + fileObj.url,
                };
                r.getCell(4).font = { color: { argb: "0000FF" }, underline: true };
            } else {
                r.getCell(4).value = "Not Uploaded";
                r.getCell(4).font = { italic: true, color: { argb: "FF0000" } };
            }
            if (row.__lastInBlock) {
                r.eachCell((cell) => {
                    cell.border = { ...cell.border, bottom: { style: "medium" } };
                });
            }
        }

        if (row.__type === "TABLE" && row.__lastInBlock) {
            r.eachCell((cell) => {
                cell.border = { ...cell.border, bottom: { style: "medium" } };
            });
        }

    });

    /* ---------------- COLUMN WIDTHS ---------------- */
    ws.columns = [
        { width: 30 },
        { width: 35 },
        { width: 40 },
        { width: 80 },
    ];

    /* ---------------- MERGES + OUTER BORDERS ---------------- */
    const sectionRanges = {};
    const subSectionRanges = {};

    rows.forEach((row, i) => {
        const excelRowIndex = i + 2; // +2 because header is row 1
        if (row.Section) {
            sectionRanges[row.Section] ??= { start: excelRowIndex, end: excelRowIndex };
            sectionRanges[row.Section].end = excelRowIndex;
        }
        if (row.SubSection) {
            const key = `${row.Section}__${row.SubSection}`;
            subSectionRanges[key] ??= { start: excelRowIndex, end: excelRowIndex };
            subSectionRanges[key].end = excelRowIndex;
        }
    });

    // Merge Section cells vertically + outer border
    Object.values(sectionRanges).forEach(({ start, end }) => {
        if (end > start) {
            ws.mergeCells(start, 1, end, 1);
            const cell = ws.getCell(start, 1);
            cell.alignment = { vertical: "middle", horizontal: "center" };
            cell.font = { bold: true };

            for (let r = start; r <= end; r++) {
                ["A", "B", "C", "D"].forEach((col) => {
                    const c = ws.getCell(`${col}${r}`);
                    c.border = {
                        top: r === start ? { style: "medium" } : c.border.top,
                        bottom: r === end ? { style: "medium" } : c.border.bottom,
                        left: col === "A" ? { style: "medium" } : c.border.left,
                        right: col === "D" ? { style: "medium" } : c.border.right,
                    };
                });
            }
        }
    });

    // Merge SubSection cells vertically + outer border
    // Merge SubSection cells vertically + outer border
    Object.values(subSectionRanges).forEach(({ start, end }) => {
        if (end > start) {
            // multi‑row subsection → merge
            ws.mergeCells(start, 2, end, 2);
            const cell = ws.getCell(start, 2);
            cell.alignment = { vertical: "middle", horizontal: "center" };
            cell.font = { italic: true };

            for (let r = start; r <= end; r++) {
                ["B", "C", "D"].forEach((col) => {
                    const c = ws.getCell(`${col}${r}`);
                    c.border = {
                        top: r === start ? { style: "medium" } : c.border.top,
                        bottom: r === end ? { style: "medium" } : c.border.bottom,
                        left: col === "B" ? { style: "medium" } : c.border.left,
                        right: col === "D" ? { style: "medium" } : c.border.right,
                    };
                });
            }
        } else {
            // single‑row subsection → no merge, but still style
            const cell = ws.getCell(start, 2);
            cell.alignment = { vertical: "middle", horizontal: "center" };
            cell.font = { italic: true };

            ["B", "C", "D"].forEach((col) => {
                const c = ws.getCell(`${col}${start}`);
                c.border = {
                    top: { style: "medium" },
                    bottom: { style: "medium" },
                    left: col === "B" ? { style: "medium" } : c.border.left,
                    right: col === "D" ? { style: "medium" } : c.border.right,
                };
            });
        }
    });

    /* ---------------- EXPORT ---------------- */
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/octet-stream" }), fileName);
}
